import { isCombinedGlobalInstanceMirrorFromTransform } from "building-types/components/componentInstanceUtils";
import type { ControllerPlacementCandidateTable } from "building-types/electrical/candidate";
import { ControllerType, LoadType } from "building-types/electrical/electricalTypesEnum";
import type {
  ControllerPlacementCandidateView,
  ControllerView,
  ElectricalSystemView,
  ElectricalSystemViewTable,
  LinkView,
  LoadView,
} from "building-types/electrical/electricalViewTypes";
import { LinkViewType } from "building-types/electrical/electricalViewTypesEnum";
import type { Controller, GeneratedElectricalGroup, Load } from "building-types/electrical/generatedElectricalTypes";
import type {
  ElectricalLayoutHandle,
  ElectricalRuleHandle,
  GroupHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import { isVirtualLoad } from "doc/settingsTools";
import type { Vec2 } from "geom/vec2";
import { defaultLoadCatalog } from "../elements/electrical/electricalTools";

export const ELECTRICAL_ELEMENT_LENGTH = 6;

/**
 * Bezier chirality for dashed load connections.
 * Instance-mirror correction matches placement only for links between two non-REF_GEOM loads;
 * any link involving a REF_GEOM-hosted load uses building-level mirror alone (REF_GEOM positions
 * ignore instance transform).
 */
function flipGuideVectorRotationForLink(
  globalMirror: boolean,
  room: Room | undefined,
  involvesReferenceGeomHost: boolean,
): boolean {
  if (involvesReferenceGeomHost) {
    return !globalMirror;
  }
  return !isCombinedGlobalInstanceMirrorFromTransform(globalMirror, room?.componentTransform);
}

export function makeElectricalSystemView(
  electrical: GeneratedElectricalGroup[],
  controllerPlacementCandidates: ControllerPlacementCandidateTable,
  levels: Table<Level>,
  rooms: Table<Room>,
  mirror: boolean,
): ElectricalSystemView {
  const view: RWTable<ElectricalSystemViewTable> = {};

  for (const levelId in levels) {
    const loadViews: LoadView[] = [];
    const controllerViews: ControllerView[] = [];
    const linkViews: LinkView[] = [];
    const controllerCandidateViews: ControllerPlacementCandidateView[] = [];

    makeCandidateViews(controllerPlacementCandidates, levelId, controllerCandidateViews);

    for (const key of Object.keys(electrical)) {
      const { handleForLinks, loads, controllers } = electrical[key];
      const loadControllerLevelLookup: { [loadIndex: number]: string } = {};

      for (const controller of controllers) {
        if (!controller?.position?.position) {
          continue;
        }
        makeControllerToLoadViews(
          controller,
          loadControllerLevelLookup,
          loads,
          levelId,
          linkViews,
          controllerViews,
          loadViews,
          rooms,
          mirror,
          handleForLinks,
        );
      }

      makeLoadToLoadViews(
        loads,
        linkViews,
        loadViews,
        levelId,
        loadControllerLevelLookup,
        rooms,
        mirror,
        handleForLinks,
      );
    }

    const table: ElectricalSystemViewTable = {
      loads: loadViews,
      controllers: controllerViews,
      links: linkViews,
      controllerCandidates: controllerCandidateViews,
    };

    view[levelId] = table;
  }

  return view;
}

function makeCandidateViews(
  controllerPlacementCandidates: ControllerPlacementCandidateTable,
  levelId: string,
  controllerCandidateViews: ControllerPlacementCandidateView[],
) {
  for (const key in controllerPlacementCandidates) {
    const roomSet = controllerPlacementCandidates[key];

    for (const placement of roomSet) {
      if (placement.candidate.levelId !== levelId) {
        continue;
      }

      controllerCandidateViews.push({
        type: placement.candidate.type,
        positionX: placement.candidate.position[0],
        positionY: placement.candidate.position[1],
        normalX: placement.candidate.normal[0],
        normalY: placement.candidate.normal[1],
        id: placement.candidate.id,
      });
    }
  }
}

function makeControllerToLoadViews(
  controller: Controller,
  loadControllerLevelLookup: { [loadIndex: number]: string },
  loads: Load[],
  levelId: string,
  linkViews: LinkView[],
  controllerViews: ControllerView[],
  loadViews: LoadView[],
  rooms: Table<Room>,
  mirror: boolean,
  handleForLinks?: GroupHandle | ElectricalLayoutHandle | ElectricalRuleHandle,
) {
  switch (controller.type) {
    case ControllerType.SWITCH: {
      break;
    }
    default: {
    }
  }
  const switchType = controller.type === ControllerType.SWITCH ? controller.switchType : undefined;
  assertIsNotNull(controller.position);

  const {
    position: { position, normal },
    loadIndex,
    levelId: controllerLevelId,
    type: controllerType,
    handle: controllerHandle,
    candidateId,
  } = controller;

  loadControllerLevelLookup[loadIndex] = controllerLevelId;

  const [x, y] = position;
  const [nx, ny] = normal;

  const loadFromController = loads[loadIndex];
  if (!loadFromController) {
    return;
  }
  const {
    position: [xLoad, yLoad],
    normal: [nxLoad, nyLoad],
    levelId: loadLevelId,
    loadType,
    handle: loadHandle,
  } = loadFromController;

  let atLeastOneItemOnCurrentFloor = false;
  if (controllerLevelId === levelId || loadLevelId === levelId) {
    atLeastOneItemOnCurrentFloor = true;
    if (!loadFromController.hideConnections) {
      if (
        (loadLevelId === levelId && loadType !== LoadType.FLOOR_TO_FLOOR_TRANSITION) ||
        controllerLevelId === levelId
      ) {
        linkViews.push({
          handle: handleForLinks,
          type: LinkViewType.ControllerLoad,
          start: { positionX: x, positionY: y, normalX: nx, normalY: ny },
          end: { positionX: xLoad, positionY: yLoad, normalX: nxLoad, normalY: nyLoad },
          flipGuideVectorRotation: flipGuideVectorRotationForLink(
            mirror,
            rooms[loadFromController.sourceRoomHandle.id],
            !!loadFromController.hostedOnReferenceGeom,
          ),
        });
      }
    }

    if (controllerLevelId === levelId) {
      controllerViews.push({
        type: controllerType,
        switchType,
        planTag: controller.planTag === "" ? undefined : controller.planTag,
        positionX: x,
        positionY: y,
        normalX: nx,
        normalY: ny,
        handle: controllerHandle,
        candidateId,
        labels: controller.labels ?? {},
      });
    } else if (atLeastOneItemOnCurrentFloor && loadType !== LoadType.FLOOR_TO_FLOOR_TRANSITION) {
      loadViews.push({
        type: LoadType.FLOOR_TO_FLOOR_TRANSITION,
        loadDescriptor: defaultLoadCatalog[LoadType.FLOOR_TO_FLOOR_TRANSITION],
        positionX: x,
        positionY: y,
        normalX: nx,
        normalY: ny,
        handle: loadHandle,
        fixControllerOverlap: true,
        levelIdIndicator: controllerLevelId,
      });
    }

    if (atLeastOneItemOnCurrentFloor && loadLevelId !== levelId) {
      loadViews.push({
        type: LoadType.FLOOR_TO_FLOOR_TRANSITION,
        loadDescriptor: defaultLoadCatalog[LoadType.FLOOR_TO_FLOOR_TRANSITION],
        positionX: xLoad,
        positionY: yLoad,
        normalX: nxLoad,
        normalY: nyLoad,
        handle: loadHandle,
        levelIdIndicator: loadLevelId,
      });
    }
  }
}

function makeLoadToLoadViews(
  loads: Load[],
  linkViews: LinkView[],
  loadViews: LoadView[],
  levelId: string,
  loadControllerLevelLookup: { [loadIndex: number]: string },
  rooms: Table<Room>,
  mirror: boolean,
  handleForLinks: GroupHandle | ElectricalLayoutHandle | ElectricalRuleHandle | undefined,
) {
  let prevLoadPosition: Vec2 | undefined;
  let prevLoadNorm: Vec2 | undefined;
  let prevLoadLevelId: string | undefined;
  let prevLoadType: string | undefined;
  let prevLoadHide: boolean | undefined;
  let prevSourceRoomId: string | undefined;
  let prevHostedOnReferenceGeom: boolean | undefined;

  for (const loadIndex of Object.keys(loads)) {
    const {
      position,
      levelId: loadLevelId,
      normal,
      loadType,
      hideConnections,
      handle: newHandle,
      loadDescriptor,
      sourceRoomHandle,
      hostedOnReferenceGeom,
    } = loads[loadIndex];
    if (!loadDescriptor || isVirtualLoad[loadType]) {
      continue;
    }

    const nextLoadIndex = parseInt(loadIndex) + 1;
    const nextLoad = (nextLoadIndex < loads.length ? loads[nextLoadIndex] : undefined) as Load;
    const [x, y] = position;
    const [nx, ny] = normal;

    let atLeastOneItemOnCurrentFloor = false;
    const eitherHidesConnections = prevLoadHide || hideConnections;

    if (!eitherHidesConnections && prevLoadPosition && (loadLevelId === levelId || prevLoadLevelId === levelId)) {
      atLeastOneItemOnCurrentFloor = true;

      if (
        (levelId === loadLevelId && loadType !== LoadType.FLOOR_TO_FLOOR_TRANSITION) ||
        (levelId === prevLoadLevelId && prevLoadType !== LoadType.FLOOR_TO_FLOOR_TRANSITION)
      ) {
        const [xPrev, yPrev] = prevLoadPosition;
        assertIsNotUndefined(prevLoadNorm);

        const [nxPrev, nyPrev] = prevLoadNorm;
        const flipRoomId = prevSourceRoomId ?? sourceRoomHandle.id;
        const involvesReferenceGeomHost = !!prevHostedOnReferenceGeom || !!hostedOnReferenceGeom;

        linkViews.push({
          handle: handleForLinks,
          type: LinkViewType.LoadLoad,
          start: { positionX: xPrev, positionY: yPrev, normalX: nxPrev, normalY: nyPrev },
          end: { positionX: x, positionY: y, normalX: nx, normalY: ny },
          flipGuideVectorRotation: flipGuideVectorRotationForLink(mirror, rooms[flipRoomId], involvesReferenceGeomHost),
        });
      }
    }

    if (loadLevelId === levelId) {
      let variableLoadId: string | undefined = loadLevelId;
      if (loadType === LoadType.FLOOR_TO_FLOOR_TRANSITION) {
        const otherLevelId = loadControllerLevelLookup[loadIndex];

        if (nextLoad && nextLoad.levelId !== levelId) {
          variableLoadId = nextLoad.levelId;
        } else if (otherLevelId !== undefined) {
          variableLoadId = otherLevelId;
        } else if (prevLoadLevelId !== levelId) {
          variableLoadId = prevLoadLevelId;
        }
      }

      loadViews.push({
        type: loadType,
        loadDescriptor,
        positionX: x,
        positionY: y,
        normalX: nx,
        normalY: ny,
        handle: newHandle,
        levelIdIndicator: variableLoadId,
      });
    } else if (atLeastOneItemOnCurrentFloor && prevLoadType !== LoadType.FLOOR_TO_FLOOR_TRANSITION) {
      loadViews.push({
        type: LoadType.FLOOR_TO_FLOOR_TRANSITION,
        loadDescriptor: defaultLoadCatalog[LoadType.FLOOR_TO_FLOOR_TRANSITION],
        positionX: x,
        positionY: y,
        normalX: nx,
        normalY: ny,
        handle: newHandle,
        levelIdIndicator: loadLevelId,
      });
    } else if (nextLoad && nextLoad.levelId === levelId && nextLoad.loadType !== LoadType.FLOOR_TO_FLOOR_TRANSITION) {
      loadViews.push({
        type: LoadType.FLOOR_TO_FLOOR_TRANSITION,
        loadDescriptor: defaultLoadCatalog[LoadType.FLOOR_TO_FLOOR_TRANSITION],
        positionX: x,
        positionY: y,
        normalX: nx,
        normalY: ny,
        handle: newHandle,
        levelIdIndicator: loadLevelId,
      });
    }

    prevLoadPosition = [x, y];
    prevLoadNorm = [nx, ny];
    prevLoadLevelId = loadLevelId;
    prevLoadType = loadType;
    prevLoadHide = hideConnections;
    prevSourceRoomId = sourceRoomHandle.id;
    prevHostedOnReferenceGeom = hostedOnReferenceGeom;
  }
}
